import React, { useState, useEffect } from 'react';
import Decimal from 'decimal.js';
import { BMP_AutomaticAlert, RealizationStrategy, BuyingStrategy } from '../../../models/bmp.model';
import { responseHero } from '../../../utils/axiosInterceptor';
import { Assets } from '../../../enums/assets';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    alertData: BMP_AutomaticAlert | null;
    onSuccess: () => void;
}

export const AlertHandlerModal: React.FC<Props> = ({ isOpen, onClose, alertData, onSuccess }) => {
    // --- HOOKOK DEKLARÁLÁSA ---
    const [exchangeRate, setExchangeRate] = useState<string>('');
    
    const [calculatedValues, setCalculatedValues] = useState<{
        fixQuantity: string;
        fixUsd: string;
        liquidityQuantity: string;
        liquidityUsd: string;
    }>({
        fixQuantity: '0.00000000',
        fixUsd: '0.00',
        liquidityQuantity: '0.00000000',
        liquidityUsd: '0.00',
    });

    // Észlelés: Vásárlás vagy Realizáció a modal kontextusa
    const isBuyingFlow = alertData?.operation === true;

    const realizationStrategy = alertData?.alertRealizationLevel;
    const buyingStrategy = alertData?.alertBuyingLevel;

    const baseQuantity = alertData?.quantity ? new Decimal(alertData.quantity) : new Decimal(0);
    const baseAmountUsd = alertData?.amount ? new Decimal(alertData.amount) : new Decimal(0);
    const assetTicker = alertData?.asset?.name || 'Crypto';
    
    const isBtcAlert = alertData?.assetType === Assets.BTC;

    // Rendszer által küldött eredeti árfolyamok
    const systemExchangeRate = alertData?.exchangeRate 
        ? new Decimal(alertData.exchangeRate).toDP(2).toString() 
        : '0.00';

    const isRateChanged = exchangeRate !== systemExchangeRate;

    useEffect(() => {
        if (alertData?.exchangeRate) {
            setExchangeRate(new Decimal(alertData.exchangeRate).toDP(2).toString());
        } else {
            setExchangeRate('0.00');
        }
    }, [alertData]);

    // Dinamikus elszámoláskezelő (Vásárlás vagy Eladás alapon)
    useEffect(() => {
        const rate = exchangeRate && !new Decimal(exchangeRate).isZero() ? new Decimal(exchangeRate) : new Decimal(0);
        
        if (isBuyingFlow) {
            // --- VÁSÁRLÁSI LOGIKA SZEKCIÓ ---
            if (rate.isZero()) return;

            // Mennyiség kiszámítása = Lekötött Keret / Árfolyam
            const purchasedQty = baseAmountUsd.div(rate);

            setCalculatedValues({
                fixQuantity: '0.00000000',
                fixUsd: '0.00',
                // A teljes vásárolt állomány a Kereskedési egyenlegre kerül
                liquidityQuantity: purchasedQty.toDP(8).toString(),
                liquidityUsd: baseAmountUsd.toDP(2).toString(),
            });

        } else {
            // --- REALIZÁCIÓS LOGIKA SZEKCIÓ (EREDETI) ---
            if (!realizationStrategy) return;

            let fixQty = new Decimal(0);
            let liqQty = new Decimal(0);

            switch (realizationStrategy) {
                case RealizationStrategy.LEVEL_1:
                    fixQty = baseQuantity.mul(0.5);
                    liqQty = baseQuantity.mul(0.5);
                    break;
                case RealizationStrategy.LEVEL_2:
                    fixQty = baseQuantity;
                    liqQty = new Decimal(0);
                    break;
                case RealizationStrategy.LEVEL_3:
                case RealizationStrategy.LEVEL_4:
                    fixQty = new Decimal(0);
                    liqQty = baseQuantity;
                    break;
                case RealizationStrategy.LEVEL_5:
                    fixQty = baseQuantity.mul(0.3);
                    liqQty = baseQuantity.mul(0.7);
                    break;
                default:
                    break;
            }

            setCalculatedValues({
                fixQuantity: fixQty.toDP(8).toString(),
                fixUsd: fixQty.mul(rate).toDP(2).toString(),
                liquidityQuantity: liqQty.toDP(8).toString(),
                liquidityUsd: liqQty.mul(rate).toDP(2).toString(),
            });
        }

    }, [exchangeRate, realizationStrategy, buyingStrategy, baseQuantity, baseAmountUsd, isBuyingFlow]);

    if (!isOpen || !alertData) return null;

    const handleResetRate = () => {
        setExchangeRate(systemExchangeRate);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const formattedRate = new Decimal(exchangeRate).toDP(2).toString();

        const payload = {
            alertId: alertData.id,
            assetType: alertData.assetType,
            depositGroupId: alertData.depositGroup_id,
            exchangeRate: formattedRate,
            strategy: isBuyingFlow ? buyingStrategy : realizationStrategy,
            fixQuantity: calculatedValues.fixQuantity,
            fixUsd: calculatedValues.fixUsd,
            liquidityQuantity: calculatedValues.liquidityQuantity,
            liquidityUsd: calculatedValues.liquidityUsd,
            marketData: {
                btc: isBtcAlert ? formattedRate : '0.00',
                eth: isBtcAlert ? '0.00' : formattedRate,
            },
            isBuying: isBuyingFlow // Segéd flag a backend részére ha szükséges
        };

        await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/automatic-alert/resolve`, { data: payload })
            .then(() => {
                onSuccess();
            })
            .catch((err) => {
                console.error("Error happened:", err);
            });

        console.log("Mentésre kész adatok (Backend formátum):", payload);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header - Színváltozás a típus függvényében */}
                <div className={`p-6 border-b border-slate-50 flex justify-between items-center ${isBuyingFlow ? 'bg-blue-50/40' : 'bg-slate-50/50'}`}>
                    <div>
                        <h3 className="text-xl font-black text-slate-800 tracking-tight">
                            {isBuyingFlow ? 'Automatikus Vásárlás Végrehajtása' : 'Automatikus Riasztás Feldolgozása'}
                        </h3>
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${isBuyingFlow ? 'text-blue-600' : 'text-amber-500'}`}>
                            {isBuyingFlow ? 'Vételi Pozíció Aktiválása és Allokáció' : 'Kereskedési Protokoll Szerinti Elszámolás'}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl transition-colors leading-none">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                        <div className="space-y-4">
                            {/* Csoport adatok */}
                            <div className="grid grid-cols-2 gap-2 p-3.5 bg-slate-50 rounded-2xl border border-slate-100 text-[11px] font-bold text-slate-600">
                                <div>
                                    <span className="block text-[8px] uppercase text-slate-400">Célcsoport:</span>
                                    <span className="text-slate-800 font-black">#{alertData.depositGroup_id} csoport</span>
                                </div>
                                <div>
                                    <span className="block text-[8px] uppercase text-slate-400">Eszköz:</span>
                                    <span className="text-slate-800 font-black">{alertData.asset?.name || assetTicker}</span>
                                </div>
                                <div className="mt-1.5">
                                    <span className="block text-[8px] uppercase text-slate-400">Stratégia Szint:</span>
                                    {isBuyingFlow ? (
                                        <span className="px-2 py-0.5 rounded bg-blue-600 text-white font-black text-[10px] inline-block mt-0.5">
                                            Vétel #{buyingStrategy?.replace('LEVEL_', '')}
                                        </span>
                                    ) : (
                                        <span className="px-2 py-0.5 rounded bg-amber-500 text-white font-black text-[10px] inline-block mt-0.5">
                                            Lépcső #{realizationStrategy?.replace('LEVEL_', '')}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5">
                                    {isBuyingFlow ? (
                                        <>
                                            <span className="block text-[8px] uppercase text-slate-400">Tervezett Keret (USD):</span>
                                            <span className="text-slate-800 font-black">${baseAmountUsd.toDP(2).toString()} USD</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="block text-[8px] uppercase text-slate-400">Teljes Pozíció:</span>
                                            <span className="text-slate-800 font-black">{baseQuantity.toDP(8).toString()} {assetTicker}</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Protokoll leírás doboz */}
                            <div className={`p-4 rounded-2xl shadow-sm text-xs text-slate-700 font-medium border ${
                                isBuyingFlow ? 'bg-blue-50/20 border-blue-100/60' : 'bg-amber-50/40 border-amber-100/70'
                            }`}>
                                <span className={`block text-[9px] font-black uppercase mb-2 tracking-wider ${isBuyingFlow ? 'text-blue-600' : 'text-amber-600'}`}>
                                    Kereskedő Protokoll utasítások:
                                </span>
                                {isBuyingFlow ? (
                                    <div className="space-y-2">
                                        <p className="font-bold text-blue-700">✓ Biztonsági Vételi Eljárás:</p>
                                        <p>1. Használj fel pontosan <b>${calculatedValues.liquidityUsd} USD</b> tőkét a végrehajtáshoz.</p>
                                        <p>2. Hajts végre egy piaci vételi megbízást a megadott célárfolyamon.</p>
                                        <p>3. A kapott várható eszközmennyiség (<b>{calculatedValues.liquidityQuantity} {assetTicker}</b>) jóváírásra kerül a számlán.</p>
                                    </div>
                                ) : (
                                    <>
                                        {realizationStrategy === RealizationStrategy.LEVEL_1 && (
                                            <div className="space-y-2">
                                                <p className="font-bold text-amber-700">✓ Részleges eladás és transzfer (+10%):</p>
                                                <p>1. <b>Coinok fele ({calculatedValues.fixQuantity} {assetTicker}):</b> belső transzfer a FIX zsebbe.</p>
                                                <p>2. <b>Másik fele ({calculatedValues.liquidityQuantity} {assetTicker}):</b> Add el piaci áron a Kereskedési zsebben.</p>
                                                <p>3. A bejövő <b>${calculatedValues.liquidityUsd} USDT</b>-t utald a Robot likviditási zsebébe.</p>
                                            </div>
                                        )}
                                        {realizationStrategy === RealizationStrategy.LEVEL_2 && (
                                            <p>A pozíció teljes egészét (<b>100%</b>), azaz mind a <b>{calculatedValues.fixQuantity} {assetTicker}</b> mennyiséget belsőleg transzferáljuk át a <b>FIX zsebbe</b>.</p>
                                        )}
                                        {(realizationStrategy === RealizationStrategy.LEVEL_3 || realizationStrategy === RealizationStrategy.LEVEL_4) && (
                                            <p>A pozíció teljes egészét (<b>100%</b>), azaz mind a <b>{calculatedValues.liquidityQuantity} {assetTicker}</b> mennyiséget add el a <b>Kereskedési zsebben</b> likviditás képzésére.</p>
                                        )}
                                        {realizationStrategy === RealizationStrategy.LEVEL_5 && (
                                            <p>A készlet 70%-át (<b>{calculatedValues.liquidityQuantity} {assetTicker}</b>) add el a Kereskedési zsebben, 30%-a pedig marad.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* JOBB OLDAL: Input és Allokáció */}
                        <div className="space-y-4">
                            
                            {/* DINAMIKUS INPUT CÍMKE */}
                            <div>
                                <label className="block text-[9px] font-black uppercase text-slate-400 mb-1">
                                    {assetTicker} {isBuyingFlow ? 'Vételi Árfolyam' : 'Vételár / Árfolyam'} (USD)
                                </label>
                                <input 
                                    type="number" 
                                    step="any" 
                                    required 
                                    className={`w-full bg-white border rounded-xl px-3 py-2 text-xs font-black outline-none focus:ring-2 transition-colors ${
                                        isRateChanged 
                                            ? 'border-amber-500 ring-amber-500/20 text-amber-900 bg-amber-50/10' 
                                            : 'border-slate-200 focus:border-slate-400 focus:ring-emerald-500/10 text-slate-800'
                                    }`} 
                                    value={exchangeRate} 
                                    onChange={(e) => setExchangeRate(e.target.value)} 
                                />
                                {isRateChanged && (
                                    <button type="button" onClick={handleResetRate} className="text-[8px] font-bold text-amber-600 hover:underline block mt-0.5">
                                        Visszaállítás az eredeti árra (${Number(systemExchangeRate).toLocaleString('en-US')})
                                    </button>
                                )}
                            </div>

                            {/* ZSEBEK DINAMIKUS ELOSZTÁSA */}
                            <div className="space-y-3 pt-1">
                                {/* Vételi elszámolás vizuális visszajelzése */}
                                {isBuyingFlow ? (
                                    <div className="p-3.5 rounded-xl bg-blue-50/40 border border-blue-100">
                                        <label className="block text-[9px] font-black uppercase text-blue-700 mb-0.5">
                                            Kereskedési számlára érkező állomány
                                        </label>
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-sm font-black text-slate-800">
                                                {calculatedValues.liquidityQuantity} <span className="text-[10px] text-slate-400">{assetTicker}</span>
                                            </span>
                                            <span className="text-xs font-black text-blue-600">
                                                -${Number(calculatedValues.liquidityUsd).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    // Eredeti eladási felosztások
                                    <>
                                        {new Decimal(calculatedValues.fixQuantity).greaterThan(0) && (
                                            <div className="p-3.5 rounded-xl bg-emerald-50/40 border border-emerald-100">
                                                <label className="block text-[9px] font-black uppercase text-emerald-700 mb-0.5">
                                                    {realizationStrategy === RealizationStrategy.LEVEL_5 ? "Kereskedési zsebben maradó rész" : "Fix zsebbe kerülő rész"}
                                                </label>
                                                <div className="flex justify-between items-baseline">
                                                    <span className="text-sm font-black text-slate-800">{calculatedValues.fixQuantity} <span className="text-[10px] text-slate-400">{assetTicker}</span></span>
                                                    <span className="text-xs font-black text-emerald-600">${Number(calculatedValues.fixUsd).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                        )}

                                        {new Decimal(calculatedValues.liquidityQuantity).greaterThan(0) && (
                                            <div className="p-3.5 rounded-xl bg-blue-50/40 border border-blue-100">
                                                <label className="block text-[9px] font-black uppercase text-blue-700 mb-0.5">
                                                    {realizationStrategy === RealizationStrategy.LEVEL_1 
                                                        ? "Robot Likviditásba transzferálandó összeg" 
                                                        : "Kereskedési zsebben realizálandó / Kereskedési likviditás"}
                                                </label>
                                                <div className="flex justify-between items-baseline">
                                                    <span className="text-sm font-black text-slate-800">{calculatedValues.liquidityQuantity} <span className="text-[10px] text-slate-400">{assetTicker}</span></span>
                                                    <span className="text-xs font-black text-blue-600">${Number(calculatedValues.liquidityUsd).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mentés gomb - Színváltás típus alapján */}
                    <div className="mt-6">
                        <button 
                            type="submit" 
                            className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-white transition-all shadow-lg active:scale-95 ${
                                isBuyingFlow ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-800 hover:bg-slate-900'
                            }`}
                        >
                            {isBuyingFlow ? 'Vásárlás Jóváhagyása' : 'Jóváhagyás'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};